import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Building2, Search, TrendingUp, ShieldCheck, ArrowRight, Briefcase } from 'lucide-react';
import { routingKeyToCounty } from '../../data/routingKeyToCounty';

const countySlugs = Array.from(
  new Set(Object.values(routingKeyToCounty).map((county) => county.toLowerCase()))
).sort();

const toTitleCase = (value: string) =>
  value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const LandlordInsightsDiscoverPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCounties = useMemo(
    () => countySlugs.filter((county) => county.includes(searchTerm.toLowerCase().trim())),
    [searchTerm]
  );

  return (
    <>
      <Helmet>
        <title>Ireland Landlord & Property Agent Reports | County pSEO Hub</title>
        <meta
          name="description"
          content="Explore county-level landlord and property agent reports across Ireland. Get actionable pricing strategy, demand signals, estimated yield ranges, and listing playbooks for every county."
        />
        <meta
          name="keywords"
          content="landlord report Ireland, property agent report Ireland, county landlord insights, letting agent strategy Ireland, investment property county report"
        />
        <link rel="canonical" href="https://rentsmart.ie/landlord-insights" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Ireland Landlord and Property Agent Reports',
            description:
              'County-level strategy reports for Irish landlords and property agents covering pricing, demand and listing decisions.',
            url: 'https://rentsmart.ie/landlord-insights',
          })}
        </script>
      </Helmet>

      <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] bg-slate-50">
        <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-emerald-900 to-cyan-900 text-white px-4 pt-16 pb-20">
          <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl" />
          <div className="relative max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 text-xs font-semibold uppercase tracking-wider mb-5">
              <Briefcase className="h-4 w-4" /> Landlord & Agent Growth Intelligence
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4">
              County Reports for Irish Landlords & Property Agents
            </h1>
            <p className="max-w-3xl mx-auto text-cyan-100 text-lg leading-relaxed mb-8">
              Use county-level strategy reports to sharpen pricing conversations, improve listing
              conversion, and identify demand-backed opportunities in Ireland.
            </p>

            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search county (e.g. dublin, kildare, galway)"
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-white text-slate-900 shadow-xl outline-none focus:ring-4 focus:ring-cyan-100"
              />
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 py-10">
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="inline-flex items-center gap-2 text-emerald-700 text-xs font-semibold uppercase tracking-wider mb-2">
                <TrendingUp className="h-4 w-4" /> Pricing Clarity
              </div>
              <p className="text-sm text-slate-700">
                Benchmark conservative, target, and premium scenarios for stronger listing decisions.
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="inline-flex items-center gap-2 text-cyan-700 text-xs font-semibold uppercase tracking-wider mb-2">
                <Building2 className="h-4 w-4" /> Local Opportunity Zones
              </div>
              <p className="text-sm text-slate-700">
                Focus on high-activity local areas with evidence-backed agent angles and playbooks.
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="inline-flex items-center gap-2 text-amber-700 text-xs font-semibold uppercase tracking-wider mb-2">
                <ShieldCheck className="h-4 w-4" /> Compliance Awareness
              </div>
              <p className="text-sm text-slate-700">
                Use practical checklists and transparent assumptions before making investment decisions.
              </p>
            </div>
          </div>

          {filteredCounties.length === 0 ? (
            <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">
              No counties match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCounties.map((countySlug) => {
                const countyName = toTitleCase(countySlug);
                return (
                  <Link
                    key={countySlug}
                    to={`/landlord-insights/${countySlug}`}
                    className="no-underline group block rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
                  >
                    <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-cyan-50 rounded-t-2xl">
                      <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-white border border-emerald-100 text-emerald-700">
                        <Building2 className="h-5 w-5" />
                      </div>
                      <h2 className="mt-4 text-2xl font-bold text-slate-900">{countyName}</h2>
                    </div>

                    <div className="p-6">
                      <p className="text-sm text-slate-600 leading-relaxed mb-5">
                        Open county strategy on yields, demand conditions, listing optimisation, and
                        landlord-agent execution guidance for {countyName}.
                      </p>
                      <div className="inline-flex items-center gap-2 font-semibold text-emerald-700 group-hover:text-emerald-600">
                        Open County Report{' '}
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default LandlordInsightsDiscoverPage;
